import { createClient } from "microcms-js-sdk";

/**
 * Class ProductFilters
 */
class ProductFilters {
	transition: number;
	limit: number;
	isFirstLoad: boolean; // 初回ロード判定用フラグ
	qParam: string; // クエリパラメータqの値を保持
	loadingElement: JQuery;
	grid: JQuery;
	productsCategoryButton: JQuery;
	productsGrid: JQuery;
	productsKeywords: JQuery;
	fieldBusiness: JQuery;
	postedSite: string;
	client: ReturnType<typeof createClient>;

	constructor(element: JQuery) {
		this.grid = element;
		this.transition = 400;
		this.limit = 12;
		this.isFirstLoad = true; // 初回ロードをtrueで初期化
		this.qParam = ""; // qParamの初期化
		this.loadingElement = this.grid.find("#js_loading");
		this.postedSite =
			(this.grid.find("#js_posted_site") as JQuery<HTMLInputElement>)?.val() || "";
		this.paramBusinessField =
			(this.grid.find("#js_business_field") as JQuery<HTMLInputElement>)?.val() || "";
		this.productsCategoryButton = this.grid.find("#js_products_category");
		this.productsGrid = this.grid.find("#js_products_grid");
		this.productsKeywords = this.grid.find("#js_products_keywords");
		this.fieldBusiness = this.productsCategoryButton
			.closest(".field-group__bottom")
			.find(".field-business");

		// microCMSクライアントの初期化
		this.client = createClient({
			serviceDomain: "3kxo8u9nf1", // microCMSのサービスドメイン
			apiKey: "c6086e40236a405c9410b8c52bed50b428f0", //画面プレビュー用
			// apiKey: 'ac14fd9441e74441bf3d27c001e7ddb131fb', //本番用
		});

		this.init();
	}

	/**
	 * Initialize
	 */
	init() {
		this.initializeEventListeners();
		this.initializeCheckboxes();
		this.initializeProducts();
	}

	/**
	 * 初期化処理: hidden要素の値をチェックボックスに反映
	 */
	initializeCheckboxes() {
		const hiddenBusinessField =
			(this.grid.find("#js_business_field") as JQuery<HTMLInputElement>)?.val() || "";

		// hidden 要素の businessField を対応するチェックボックスに反映
		this.grid.find(".field-checkbox__input").each((_, checkbox) => {
			const $checkbox = $(checkbox) as JQuery<HTMLInputElement>;
			if ($checkbox.val() === hiddenBusinessField) {
				$checkbox.prop("checked", true);
			}
		});
	}

	/**
	 * 初期化処理
	 * 初回ロード時にクエリパラメータからページ番号、qを取得
	 */
	initializeProducts() {
		const urlParams = new URLSearchParams(window.location.search);
		const pageParam = urlParams.get("page");
		const page = pageParam ? parseInt(pageParam, 10) : 1; // クエリパラメータからpageを取得 (デフォルト: 1)
		this.qParam = urlParams.get("q") || ""; // クエリパラメータqをクラス変数に代入
		// qクエリパラメータが存在する場合、検索窓に値を設定
		if (this.qParam) {
			const searchInput = this.productsKeywords as JQuery<HTMLInputElement>;
			if (searchInput.length) {
				searchInput.val(this.qParam);
			}
		}

		this.fetchProducts(page); // 初回ロード時に適切なページ番号でリクエスト
	}

	/**
	 * Set up event listeners
	 */
	initializeEventListeners() {
		const searchInput = this.grid.find("#js_products_keywords") as JQuery<HTMLInputElement>;
		const searchButton = this.grid.find("#js_products_keywords_button");
		const checkboxes = this.grid.find(".field-checkbox__input");

		// Enterキーで検索
		searchInput.on("keypress", (event) => {
			if (event.key === "Enter") {
				event.preventDefault();
				this.fetchProducts(1);
			}
		});

		// 検索ボタンで検索
		searchButton.on("click", () => {
			this.fetchProducts(1);
		});

		// フィルタチェックボックスの変更で再検索
		checkboxes.on("change", () => {
			this.fetchProducts(1);
		});

		this.productsCategoryButton.on("click", this.toggleFieldBusiness.bind(this));
	}

	/**
	 * Toggle field-business visibility
	 * @param event
	 */
	toggleFieldBusiness(event: JQuery.ClickEvent) {
		event.stopPropagation();
		const isVisible = this.fieldBusiness.is(":visible");
		const parent = this.grid.find(".field-select");

		if (isVisible) {
			this.fieldBusiness.slideUp(this.transition);
			parent.removeClass("is_open");
		} else {
			this.fieldBusiness.slideDown(this.transition);
			parent.addClass("is_open");
		}
	}

	/**
	 * Fetch products from the API
	 * @param page
	 */
	async fetchProducts(page: number) {
		// キーワードの最新値を取得し、空の場合はqParamをリセット
		const keyword = (this.productsKeywords as JQuery<HTMLInputElement>)?.val() || "";
		this.qParam = keyword || ""; // 検索窓が空の場合にqParamをリセット

		// URLを更新 (qパラメータを反映)
		const url = new URL(window.location.href);
		if (this.qParam) {
			url.searchParams.set("q", this.qParam); // 検索キーワードを更新
		} else {
			url.searchParams.delete("q"); // qが空の場合は削除
		}
		url.searchParams.set("page", page.toString()); // ページ番号を更新
		history.replaceState(null, "", url.toString()); // アドレスバーを更新

		// filters設定
		// const postedSite =
		// 	(this.grid.find("#js_posted_site") as JQuery<HTMLInputElement>)?.val() || "";
		let selectedFields: string[];
		if (this.paramBusinessField) {
			// this.paramBusinessFieldが存在する場合、それを使用
			selectedFields = [this.paramBusinessField];
		} else {
			// this.paramBusinessFieldが存在しない場合、チェックボックスの選択状態を使用
			selectedFields = this.grid
				.find(".field-checkbox__input:checked")
				.map((_, checkbox) => $(checkbox).val())
				.get() as string[];
		}

		const filters = this.createFilterQuery(this.postedSite, selectedFields);
		// console.log(filters);

		// loadingを表示
		this.productsGrid.removeClass("is_show");
		this.loadingElement.addClass("is_show");

		// APIリクエスト
		try {
			const data = await this.client.get({
				endpoint: "toray_products",
				queries: {
					filters,
					q: keyword, // キーワードを指定
					orders: "displayOrder",
					fields: "id,postedSite,businessSegment,businessField,applications,productName,commonName,productImages,businessSiteLink",
					limit: this.limit,
					offset: (page - 1) * this.limit,
				},
			});
			this.updateProductGrid(data);
		} catch (error) {
			console.error("Error fetching products:", error);
		} finally {
			// loading非表示
			setTimeout(() => {
				this.productsGrid.addClass("is_show");
				this.loadingElement.removeClass("is_show");
			}, this.transition);
		}
	}

	/**
	 * Create filter query
	 * @param postedSite
	 * @param businessFields
	 */
	createFilterQuery(postedSite: string, businessFields: string[]): string {
		const siteFilter = postedSite ? `postedSite[contains]${postedSite}` : "";
		const fieldFilter = businessFields.length
			? `(${businessFields.map((field) => `businessField[contains]${field}`).join("[or]")})`
			: "";

		// `siteFilter` と `fieldFilter` のいずれかが存在しない場合も安全に処理
		return [siteFilter, fieldFilter].filter(Boolean).join("[and]");
	}

	/**
	 * Update product grid with API data
	 * @param data - APIレスポンスデータ
	 */
	updateProductGrid(data: any) {
		const lang = (this.grid.find("#js_language") as JQuery<HTMLInputElement>)?.val() || "en"; // 言語設定を取得
		this.productsGrid.empty();

		// レスポンスのtotalCountが0の場合
		if (data.totalCount === 0) {
			const noResultMessage = `
				<div class="no-result-message">
					<p>There are no applicable products.</p>
				</div>
			`;
			this.productsGrid.html(noResultMessage);
			return; // 処理を終了
		}

		// レスポンスデータがある場合はグリッドを作成
		data.contents.forEach((item: any) => {
			const itemId = item.id;

			// productName
			let productNameObj = item.productName.find(
				(pn: any) =>
					pn.lang?.includes(lang) &&
					(pn.site.length === 0 || pn.site.includes(this.postedSite)),
			);
			if (!productNameObj) {
				productNameObj = item.productName.find((pn: any) => pn.lang?.includes(lang));
			}
			if (!productNameObj) {
				productNameObj = item.productName.find(
					(pn: any) => pn.lang === "en" && pn.site.includes("global"),
				);
			}
			const productName = productNameObj?.text || "";

			// commonName
			let commonNameObj = item.commonName.find((cn: any) => cn.lang?.includes(lang));
			if (!commonNameObj) {
				commonNameObj = item.commonName.find((cn: any) => cn.lang === "en");
			}
			const commonName = commonNameObj?.text || "";

			// productImages
			const productImages =
				item.productImages && item.productImages.length > 0
					? item.productImages[0]?.url
					: "https://images.microcms-assets.io/assets/fa7ebd3c9a484c6197223084f64eb8d7/6bbeebcd64a04eb5b0be2ca01e6daba6/products_noimage.jpg";

			// businessSiteLink
			let businessSiteLinkObj = item.businessSiteLink.find(
				(link: any) => link.lang?.includes(lang) && link.site?.includes(this.postedSite),
			);
			if (!businessSiteLinkObj) {
				businessSiteLinkObj = item.businessSiteLink.find((link: any) =>
					link.lang?.includes(lang),
				);
			}

			const businessSiteLinkUrl =
				businessSiteLinkObj?.url || `/us/products/search/details.html?contentId=${itemId}`;
			let linkClass = "";
			let linkTarget = "";

			// URLの設定
			if (businessSiteLinkUrl.startsWith("http")) {
				linkClass = "_iconWindow";
				linkTarget = ' target="_blank"';
			}

			console.log(productName);

			// カードHTMLの作成
			const productCard = `
				<div class="product-card has-animated-circle" data-contentid="${itemId}">
					<div class="product-card__image-container">
						<a href="${businessSiteLinkUrl}" ${linkTarget}>
							<figure class="product-card__figure has-scale-animation">
								<img fetchpriority="high" decoding="async" width="640" height="400"
									src="${productImages}" class="product-card__image" alt="${productName}" sizes="(max-width: 640px) 100vw, 640px">
							</figure>
						</a>
					</div>
					<div class="product-card__content">
						<span class="animated-circle" style="--circle-width: 32px; --half-circle-width: 16px;">
							<span class="animated-circle__side animated-circle__side--right">
								<span class="animated-circle__inner animated-circle__inner--right"></span>
							</span>
							<span class="animated-circle__side animated-circle__side--left">
								<span class="animated-circle__inner animated-circle__inner--left"></span>
							</span>
							<span class="animated-circle__icon">
								<i class="icon-large-arrow-right"></i>
							</span>
						</span>
						<h2 class="product-card__title has-t-5-bold-font-size">
							<a href="${businessSiteLinkUrl}"${linkTarget}>${productName}</a>
						</h2>
						<p>${commonName}</p>
					</div>
				</div>
			`;

			// グリッドにカードを追加
			this.productsGrid.append(productCard);
		});

		// ページネイションを生成
		const currentPage = Math.floor(data.offset / data.limit) + 1; // 現在のページを計算
		this.generatePagination(currentPage, data.limit, data.offset, data.totalCount);

		// 初回ロード以外の場合はスクロール
		if (!this.isFirstLoad) {
			if (this.productsGrid.length) {
				// スムーススクロールではなく、直接スクロール位置を設定
				window.scrollTo({
					top: this.productsGrid.offset()?.top || 0,
				});
			}
		}

		// 初回ロード後はフラグをfalseに変更
		this.isFirstLoad = false;
	}

	/**
	 * Generate pagination and append it to the DOM
	 * @param currentPage - 現在のページ番号
	 * @param limit       - 1ページあたりの表示件数
	 * @param offset      - 現在のデータの開始位置
	 * @param totalCount  - 総データ件数
	 */
	generatePagination(currentPage: number, limit: number, offset: number, totalCount: number) {
		const totalPages = Math.ceil(totalCount / limit); // 総ページ数を計算
		const paginationElement = this.grid.find("#js_products_pagination");
		paginationElement.empty(); // 既存のページネイションをクリア

		if (totalPages <= 1) return; // ページが1つだけならページネイションは不要

		// ページURLを生成するヘルパー関数
		const generatePageUrl = (page: number): string => {
			return `./?page=${page}`;
		};

		// 左矢印 (前のページ)
		const prevPage = currentPage > 1 ? currentPage - 1 : null;

		// 右矢印 (次のページ)
		const nextPage = currentPage < totalPages ? currentPage + 1 : null;

		// ページ番号リンクの生成
		let pageLinks = "";
		for (let i = 1; i <= totalPages; i++) {
			const pageUrl = generatePageUrl(i);
			const activeClass = i === currentPage ? "active" : "";
			pageLinks += `<a href="${pageUrl}" data-page="${i}"><span class="pagination__number ${activeClass}">${i}</span></a>`;
		}

		// ページネイションHTML
		const paginationHtml = `
			<div class="pagination">
				<div class="pagination__left">
					${prevPage ? `<a href="${generatePageUrl(prevPage)}" data-page="${prevPage}"><i class="icon-arrow-left"></i></a>` : ""}
				</div>
				<div class="pagination__center">
					${pageLinks}
				</div>
				<div class="pagination__right">
					${nextPage ? `<a href="${generatePageUrl(nextPage)}" data-page="${nextPage}"><i class="icon-arrow-right"></i></a>` : ""}
				</div>
			</div>
		`;

		// ページネイションをDOMに追加
		paginationElement.append(paginationHtml);

		// ページネイションクリックイベントを設定
		paginationElement.find("a").on("click", (event) => {
			event.preventDefault();
			const targetPage = $(event.currentTarget).data("page");
			if (targetPage) {
				this.fetchProducts(targetPage); // 選択されたページでfetchProductsを呼び出す
			}
		});
	}

	/**
	 * Debounce helper function
	 * @param func
	 * @param wait
	 */
	debounce(func: () => void, wait: number): () => void {
		let timeout: number;
		return () => {
			clearTimeout(timeout);
			timeout = window.setTimeout(func, wait);
		};
	}
}

// クラスインスタンスの作成
const productsFilter: ProductFilters[] = [];
$(".block-product-grid-filters").each(function () {
	const filterInstance = new ProductFilters($(this));
	productsFilter.push(filterInstance);
});
